<template>
  <div class="tracker">
    <header class="topbar">
      <button class="brand" @click="backMainPanel" aria-label="Ir al panel principal">
        <img src="@/assets/logogruas.png" alt="Home" />
        <div class="brand__copy">
          <span class="brand__title">TRACKER</span>
          <span class="brand__sub">Panel de monitoreo</span>
        </div>
      </button>
      <div class="topbar__status">
        <button class="chip solid" @click="fetchLocations" :disabled="isRefreshing">
          {{ isRefreshing ? 'Actualizando...' : 'Actualizar' }}
        </button>
        <button class="chip alert-btn" :class="{ pulse: hasUnattendedAlerts }" @click="alertsOpen = !alertsOpen">
          Mostrar alertas ({{ alertedCount }})
        </button>
        <span class="status-badge" :data-state="connectionState">{{ connectionText }}</span>
        <span class="muted">Auto: {{ autoRefreshSeconds }}s</span>
        <span class="muted">Ult. sync: {{ lastSyncLabel }}</span>
      </div>
      <div class="topbar__actions">
        <div class="command">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M10 4a6 6 0 1 1 3.53 10.86l3.8 3.8-1.41 1.41-3.8-3.8A6 6 0 0 1 10 4m0 2a4 4 0 1 0 .001 8.001A4 4 0 0 0 10 6"/></svg>
          <input ref="searchInputRef" v-model.trim="searchQuery" type="text" placeholder="Buscar unidad (Cmd/Ctrl + K)" />
        </div>
        <button class="icon-btn" @click="detailOpen = !detailOpen" :aria-pressed="detailOpen" aria-label="Alternar panel de unidad">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 5h16a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1m0 2v10h10V7H4m12 0v10h4V7h-4"/></svg>
        </button>
        <button class="icon-btn" @click="toggleFullscreen" :aria-pressed="isFullscreen" aria-label="Alternar pantalla completa"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 14H5v5h5v-2H7v-3m0-4h3V7h2v5H7V10m10 7h-3v2h5v-5h-2v3m0-10V5h-5v2h3v3h2V7z"/></svg></button>
      </div>
    </header>

    <div class="layout" :class="{ 'detail-hidden': !detailOpen }">
      <aside class="side">
        <div class="side__header"><h3>Unidades</h3><span class="count">{{ filteredUnits.length }}</span></div>
        <div class="filters">
          <button class="chip" :class="{ active: speedFilter === 'all' }" @click="speedFilter = 'all'">Todas</button>
          <button class="chip" :class="{ active: speedFilter === 'stopped' }" @click="speedFilter = 'stopped'">Detenidas</button>
          <button class="chip" :class="{ active: speedFilter === 'moving' }" @click="speedFilter = 'moving'">En mov.</button>
          <button class="chip" :class="{ active: onlyAlerts }" @click="onlyAlerts = !onlyAlerts">Solo alertas</button>
        </div>
        <div class="status-filters">
          <button class="status-pill" :class="{ active: statusFilter === 'all' }" @click="statusFilter = 'all'">Todos</button>
          <button v-for="status in statusOptions" :key="status.value" class="status-pill" :class="{ active: statusFilter === status.value }" :data-tone="status.tone" @click="statusFilter = status.value">{{ status.label }}</button>
        </div>
        <div class="list">
          <button v-for="unit in filteredUnits" :key="unit.key" class="list__item" :class="{ active: selectedUnitId === unit.key, focused: highlightedUnit?.key === unit.key }" @click="selectUnit(unit)">
            <div class="avatar" :data-state="unit.speed > 0 ? 'moving' : 'stopped'">🚛</div>
            <div class="meta">
              <div class="title">{{ unit.vehicle_name || unit.unit_short_id || unit.vehicle_plate }} <span class="status-chip" :data-tone="unit.statusMeta.tone">{{ unit.statusMeta.label }}</span></div>
              <div class="sub">{{ unit.vehicle_plate || 'Sin placas' }}</div>
              <div class="sub">Nota: {{ unit.noteNumber || '—' }} <span v-if="unit.operatorName">• {{ unit.operatorName }}</span></div>
            </div>
            <div class="right"><span class="badge">{{ unit.speed }} km/h</span></div>
          </button>
          <div v-if="!filteredUnits.length" class="empty">Sin coincidencias</div>
        </div>
      </aside>

      <main class="mapwrap">
        <div v-if="fetchError || staleData" class="stale-banner">
          {{ fetchError ? 'Sin conexion con el servidor de ubicaciones.' : 'Los datos estan desactualizados.' }}
          <span class="stale-meta">{{ networkMetaText }}</span>
        </div>
        <Map ref="mapRef" :center="mapCenter" :zoom="12" map-type-id="roadmap" :options="mapOptions" class="map">
          <Marker v-for="unit in mapUnits" :key="unit.key" :position="{ lat: num(unit.lat), lng: num(unit.long) }" :icon="markerIcon(unit)" :label="markerLabel(unit)" @click="selectUnit(unit)" />
          <Marker
            v-if="selectedUnit && selectedUnit.hasCoords && selectedPulseIcon"
            :position="{ lat: num(selectedUnit.lat), lng: num(selectedUnit.long) }"
            :icon="selectedPulseIcon"
            :z-index="1"
          />
        </Map>
        <div class="overlay">
          <div class="cards">
            <div class="stat"><span class="kpi">{{ processedUnits.length }}</span><span class="lbl">Unidades</span></div>
            <div class="stat" data-tone="ok"><span class="kpi">{{ movingCount }}</span><span class="lbl">En movimiento</span></div>
            <div class="stat" data-tone="warn"><span class="kpi">{{ stoppedCount }}</span><span class="lbl">Detenidas</span></div>
            <div class="stat" data-tone="critical"><span class="kpi">{{ alertedCount }}</span><span class="lbl">Alertas</span></div>
            <div class="stat" data-tone="available"><span class="kpi">{{ availableCount }}</span><span class="lbl">Disponible</span></div>
            <div class="stat" data-tone="busy"><span class="kpi">{{ busyCount }}</span><span class="lbl">Ocupada</span></div>
            <div class="stat" data-tone="service"><span class="kpi">{{ serviceCount }}</span><span class="lbl">En servicio</span></div>
            <div class="kpi-help-wrap">
              <button class="kpi-help-btn" type="button" aria-label="Explicación de KPIs">¿Qué significa?</button>
              <div class="kpi-help-tooltip" role="note">
                <p><strong>Unidades:</strong> cuenta de unidades activas.</p>
                <p><strong>En movimiento:</strong> unidades con velocidad diferente de 0.</p>
                <p><strong>Detenidas:</strong> unidades con velocidad actual de 0.</p>
                <p><strong>Alertas:</strong> unidades con alguna consideración operativa (ver alertas).</p>
                <p><strong>Disponible:</strong> unidades sin servicio asignado.</p>
                <p><strong>Ocupada:</strong> unidades con 3 o más servicios en cola.</p>
                <p><strong>En servicio:</strong> unidades realizando un servicio y con menos de 3 servicios en cola.</p>
              </div>
            </div>
          </div>
        </div>
      </main>

      <aside v-if="detailOpen" class="detail">
        <div class="detail__header"><h3>Unidad seleccionada</h3><span class="muted" v-if="selectedUnit">Ult. vista: {{ selectedUnitLastSeenLabel }}</span></div>
        <div v-if="selectedUnit" class="detail__body">
          <div class="title-row"><h4>{{ selectedUnit.vehicle_name || selectedUnit.unit_short_id }}</h4><span class="status-chip" :data-tone="selectedUnit.statusMeta.tone">{{ selectedUnit.statusMeta.label }}</span></div>
          <div class="meta-grid">
            <div class="cell"><span>Placas</span><strong>{{ selectedUnit.vehicle_plate || '—' }}</strong></div>
            <div class="cell"><span>Velocidad</span><strong>{{ selectedUnit.speed }} km/h</strong></div>
            <div class="cell"><span>Operador</span><strong>{{ selectedUnit.operatorName || 'Sin asignar' }}</strong></div>
            <div class="cell"><span>Nota</span><strong>{{ selectedUnit.noteNumber || '—' }}</strong></div>
          </div>
          <div class="actions">
            <button class="btn" @click="centerSelectedUnit">Centrar</button>
            <button class="btn" :class="{ active: followMode }" @click="followMode = !followMode">{{ followMode ? 'Siguiendo' : 'Seguir unidad' }}</button>
            <button class="btn" @click="copySelectedUnitData">Copiar datos</button>
            <button v-if="selectedUnitNoteId" class="btn note-btn" @click="openSelectedNote">Ver nota</button>
          </div>
        </div>
        <div v-else class="empty">Selecciona una unidad en la lista o en el mapa.</div>
      </aside>
    </div>

    <transition name="alert-pop">
      <div v-if="alertsOpen" class="alerts-popup" role="dialog" aria-label="Alertas activas">
        <div class="alerts-popup__head">
          <h4>Alertas activas ({{ alertedCount }})</h4>
          <button class="icon-btn close-btn" @click="alertsOpen = false" aria-label="Cerrar alertas">✕</button>
        </div>
        <div v-if="globalAlerts.length" class="alerts-popup__list">
          <button
            v-for="alert in globalAlerts"
            :key="`${alert.unitKey}-${alert.code}`"
            class="item"
            :data-severity="alert.severity"
            @click="selectAlertUnit(alert.unitKey)"
          >
            {{ alert.unitLabel }}: {{ alert.label }}
          </button>
        </div>
        <p v-else class="empty-inline">Sin alertas activas</p>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { Map, Marker } from '@fawmi/vue-google-maps'
import axios from 'axios'

const autoRefreshMs = 30000
const autoRefreshSeconds = autoRefreshMs / 1000
const OVERSPEED_THRESHOLD = 90

const STATUS_META = {
  1: { value: '1', label: 'Disponible', tone: 'ok' },
  2: { value: '2', label: 'Ocupada', tone: 'warn' },
  3: { value: '3', label: 'Fuera de servicio', tone: 'critical' },
  4: { value: '4', label: 'En servicio', tone: 'info' },
  5: { value: '5', label: 'Mantenimiento', tone: 'neutral' },
  default: { value: '0', label: 'Sin estatus', tone: 'neutral' }
}
const statusOptions = Object.values(STATUS_META).filter((s) => !['0', '3', '5'].includes(s.value))
const mapOptions = { gestureHandling: 'greedy', scrollwheel: true, zoomControl: true, fullscreenControl: false }

const mapRef = ref(null)
const timerId = ref(null)
let pulseTimerId = null
const searchInputRef = ref(null)
const rawUnits = ref([])
const mapCenter = ref({ lat: 25.66831199290133, lng: -100.2802354639708 })
const searchQuery = ref('')
const selectedUnitId = ref('')
const speedFilter = ref('all')
const statusFilter = ref('all')
const onlyAlerts = ref(false)
const followMode = ref(false)
const highlightedIndex = ref(0)
const isFullscreen = ref(Boolean(document.fullscreenElement))
const alertsOpen = ref(false)
const detailOpen = ref(true)
const pulseTick = ref(0)
const isRefreshing = ref(false)
const fetchError = ref('')
const lastSuccessfulSync = ref(0)
const lastFetchDurationMs = ref(0)
const consecutiveFailures = ref(0)

const num = (v) => parseFloat(v)
const speedVal = (u) => Number(u?.vehicle_speed) || 0
const statusVal = (u) => Number(u?.unit_status)
const isAllowedStatus = (u) => ![3, 5].includes(statusVal(u))
const hasCoords = (u) => Number.isFinite(num(u?.lat)) && Number.isFinite(num(u?.long))
const unitKey = (u) => String(u?.unit_short_id || u?.vehicle_plate || u?.vehicle_name || '')
const assignationNoteNumber = (u) => u?.assignations?.[0]?.assistance_note?.note_number || ''
const assignationOperatorName = (u) =>
  u?.assignations?.[0]?.operator?.employee_first_name ||
  u?.current_assigned_operator_name ||
  ''
const assignedOperatorVal = (u) => u?.current_assigned_operator

const isMatchedUnit = (u) => {
  const unitId = String(u?.unit_short_id || '').trim()
  const vehicleName = String(u?.vehicle_name || '').trim()
  return Boolean(unitId && vehicleName && unitId === vehicleName)
}
const deriveAlerts = (u) => {
  const alerts = []
  const speed = speedVal(u)
  if (speed > OVERSPEED_THRESHOLD) alerts.push({ code: 'speed_over_threshold', label: `Velocidad alta (${speed} km/h)`, severity: 'medium' })
  if (!assignedOperatorVal(u)) alerts.push({ code: 'missing_operator', label: 'Sin operador asignado', severity: 'low' })
  return alerts
}
const priorityFromAlerts = (alerts) => alerts.reduce((acc, alert) => acc + (alert.severity === 'high' ? 30 : alert.severity === 'medium' ? 12 : 4), 0)

const processedUnits = computed(() => rawUnits.value.map((u) => ({
  ...u,
  key: unitKey(u),
  speed: speedVal(u),
  statusMeta: STATUS_META[statusVal(u)] || STATUS_META.default,
  noteNumber: assignationNoteNumber(u),
  operatorName: assignationOperatorName(u),
  lastSeenAt: 0,
  hasCoords: hasCoords(u),
  alerts: deriveAlerts(u),
  displayPriority: priorityFromAlerts(deriveAlerts(u)) + (speedVal(u) > 0 ? 1 : 0)
})).sort((a, b) => b.displayPriority - a.displayPriority))

const filteredUnits = computed(() => {
  const q = searchQuery.value.trim().toLowerCase()
  return processedUnits.value.filter((unit) => {
    if (q) {
      const searchable = [unit.vehicle_name, unit.vehicle_plate, unit.operatorName, unit.noteNumber, unit.unit_short_id].filter(Boolean).join(' ').toLowerCase()
      if (!searchable.includes(q)) return false
    }
    if (speedFilter.value === 'moving' && unit.speed <= 0) return false
    if (speedFilter.value === 'stopped' && unit.speed > 0) return false
    if (statusFilter.value !== 'all' && String(unit.unit_status) !== statusFilter.value) return false
    if (onlyAlerts.value && !unit.alerts.length) return false
    return true
  })
})
const mapUnits = computed(() => filteredUnits.value.filter((u) => u.hasCoords && isAllowedStatus(u)))
const highlightedUnit = computed(() => filteredUnits.value[highlightedIndex.value] || null)
const selectedUnit = computed(() => processedUnits.value.find((u) => u.key === selectedUnitId.value) || null)
const selectedUnitNoteId = computed(() =>
  selectedUnit.value?.assignations?.[0]?.assistance_note?.note_ident ||
  selectedUnit.value?.assignations?.[0]?.assistance_note?.id ||
  ''
)
const movingCount = computed(() => processedUnits.value.filter((u) => u.speed > 0).length)
const stoppedCount = computed(() => processedUnits.value.filter((u) => u.speed === 0).length)
const availableCount = computed(() => processedUnits.value.filter((u) => statusVal(u) === 1).length)
const busyCount = computed(() => processedUnits.value.filter((u) => statusVal(u) === 2).length)
const serviceCount = computed(() => processedUnits.value.filter((u) => statusVal(u) === 4).length)
const globalAlerts = computed(() =>
  filteredUnits.value.flatMap((unit) =>
    unit.alerts.map((alert) => ({
      ...alert,
      unitKey: unit.key,
      unitLabel: unit.vehicle_name || unit.unit_short_id || unit.vehicle_plate || unit.key
    }))
  )
)
const alertedCount = computed(() => globalAlerts.value.length)
const hasUnattendedAlerts = computed(() => alertedCount.value > 0 && !alertsOpen.value)
const staleData = computed(() => lastSuccessfulSync.value && Date.now() - lastSuccessfulSync.value > autoRefreshMs * 2)
const lastSyncLabel = computed(() => (lastSuccessfulSync.value ? new Date(lastSuccessfulSync.value).toLocaleTimeString('es-MX', { hour12: false }) : 'Sin datos'))
const connectionState = computed(() => (fetchError.value ? 'error' : staleData.value ? 'warn' : 'ok'))
const connectionText = computed(() => (fetchError.value ? 'Sin conexion' : staleData.value ? 'Datos antiguos' : 'En linea'))
const networkMetaText = computed(() => `Intentos fallidos: ${consecutiveFailures.value} | T fetch: ${lastFetchDurationMs.value || 'n/a'} ms`)
const selectedUnitLastSeenLabel = computed(() => {
  const ts = selectedUnit.value?.lastSeenAt
  if (!ts) return 'Sin marca de tiempo'
  const diffMs = Date.now() - ts
  if (diffMs < 60000) return 'Hace menos de 1 min'
  if (diffMs < 3600000) return `Hace ${Math.floor(diffMs / 60000)} min`
  return new Date(ts).toLocaleString('es-MX', { hour12: false })
})

const selectUnit = (unit) => {
  if (!unit) return
  selectedUnitId.value = unit.key
  detailOpen.value = true
  if (unit.hasCoords) mapCenter.value = { lat: num(unit.lat), lng: num(unit.long) }
}
const centerSelectedUnit = () => { if (selectedUnit.value?.hasCoords) mapCenter.value = { lat: num(selectedUnit.value.lat), lng: num(selectedUnit.value.long) } }
const selectAlertUnit = (key) => { const unit = processedUnits.value.find((u) => u.key === key); if (unit) selectUnit(unit) }
const openSelectedNote = () => {
  if (!selectedUnitNoteId.value) return
  window.open(`/notes/${encodeURIComponent(String(selectedUnitNoteId.value))}`, '_blank')
}
const markerIcon = (unit) => {
  const status = statusVal(unit)
  if (status === 4) return require('@/assets/grua-map-icon-marker_blue-car.png')
  if (status === 1) return require('@/assets/grua-map-icon-marker_green-car.png')
  if (status === 2) return require('@/assets/grua-map-icon-marker.png')
  return require('@/assets/grua-map-icon-marker.png')
}
const markerLabel = (unit) => ({ text: String(unit.unit_short_id || ''), color: '#111827', fontSize: '10px', fontWeight: '700' })
const selectedPulseIcon = computed(() => {
  if (!window.google?.maps?.SymbolPath) return null
  const phase = pulseTick.value % 12
  const scale = 13 + phase * 0.95
  const opacity = 0.72 - phase * 0.05
  return {
    path: window.google.maps.SymbolPath.CIRCLE,
    scale,
    fillColor: '#facc15',
    fillOpacity: Math.max(opacity, 0.16),
    strokeColor: '#1e3a8a',
    strokeOpacity: Math.max(opacity, 0.24),
    strokeWeight: 3
  }
})

const fetchLocations = async () => {
  const startedAt = Date.now()
  isRefreshing.value = true
  try {
    const { data } = await axios.get(`${process.env.API_DIRECTORY}/get-tows-status`)
    const raw = Array.isArray(data) ? data : []
    rawUnits.value = raw.filter((u) => isMatchedUnit(u) && isAllowedStatus(u) && hasCoords(u))
    lastSuccessfulSync.value = Date.now()
    fetchError.value = ''
    consecutiveFailures.value = 0
    if (!selectedUnitId.value && processedUnits.value.length) selectedUnitId.value = processedUnits.value[0].key
    if (selectedUnitId.value && followMode.value) centerSelectedUnit()
  } catch (error) {
    consecutiveFailures.value += 1
    fetchError.value = error?.message || 'Error de red'
  } finally {
    lastFetchDurationMs.value = Date.now() - startedAt
    isRefreshing.value = false
  }
}

const resizeMap = async () => {
  await nextTick()
  const map = await mapRef.value?.$mapPromise
  if (map && window.google?.maps?.event) { window.google.maps.event.trigger(map, 'resize'); map.setCenter(mapCenter.value) }
}
const moveHighlight = (step) => { if (filteredUnits.value.length) highlightedIndex.value = Math.min(filteredUnits.value.length - 1, Math.max(0, highlightedIndex.value + step)) }
const jumpToHighlighted = () => { if (highlightedUnit.value) selectUnit(highlightedUnit.value) }
const copySelectedUnitData = async () => {
  if (!selectedUnit.value) return
  const payload = [`Unidad: ${selectedUnit.value.vehicle_name || selectedUnit.value.unit_short_id || '—'}`, `Placas: ${selectedUnit.value.vehicle_plate || '—'}`, `Estatus: ${selectedUnit.value.statusMeta.label}`, `Velocidad: ${selectedUnit.value.speed} km/h`, `Operador: ${selectedUnit.value.operatorName || 'Sin asignar'}`, `Nota: ${selectedUnit.value.noteNumber || '—'}`].join('\n')
  try { await navigator.clipboard.writeText(payload) } catch (error) { console.error(error) }
}
const backMainPanel = () => { window.location.href = '/main-panel' }
const toggleFullscreen = async () => { try { if (!document.fullscreenElement) await document.documentElement.requestFullscreen(); else await document.exitFullscreen(); isFullscreen.value = Boolean(document.fullscreenElement) } catch (error) { console.error(error) } }
const shouldIgnoreShortcuts = () => { const tag = document.activeElement?.tagName?.toLowerCase(); return tag === 'input' || tag === 'textarea' || document.activeElement?.isContentEditable }
const onWindowKeydown = (event) => {
  const key = event.key.toLowerCase()
  if ((event.ctrlKey || event.metaKey) && key === 'k') { event.preventDefault(); searchInputRef.value?.focus(); return }
  if (shouldIgnoreShortcuts()) return
  if (key === 'j') { event.preventDefault(); moveHighlight(1); return }
  if (key === 'k') { event.preventDefault(); moveHighlight(-1); return }
  if (key === 'enter') { event.preventDefault(); jumpToHighlighted(); return }
  if (key === 'f') { event.preventDefault(); followMode.value = !followMode.value }
  if (key === 'a') { event.preventDefault(); alertsOpen.value = !alertsOpen.value }
}

watch(selectedUnit, (unit) => { if (unit && unit.hasCoords && followMode.value) mapCenter.value = { lat: num(unit.lat), lng: num(unit.long) } })
watch(detailOpen, () => { resizeMap() })
onMounted(() => {
  fetchLocations()
  timerId.value = setInterval(fetchLocations, autoRefreshMs)
  pulseTimerId = setInterval(() => { pulseTick.value += 1 }, 120)
  window.addEventListener('resize', resizeMap)
  window.addEventListener('keydown', onWindowKeydown)
})
onUnmounted(() => {
  if (timerId.value) clearInterval(timerId.value)
  if (pulseTimerId) clearInterval(pulseTimerId)
  window.removeEventListener('resize', resizeMap)
  window.removeEventListener('keydown', onWindowKeydown)
})
</script>

<style>
*, *::before, *::after { box-sizing: border-box; }
.gm-style, .gm-style > div, .gm-style iframe { image-rendering: auto; }
</style>

<style scoped>
.tracker { min-height: 100vh; background: #e4e9f1; color: #111827; }
.topbar { position: sticky; top: 0; z-index: 40; min-height: 72px; display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 10px 12px; background: radial-gradient(1200px 200px at 10% -20%, rgba(255,255,255,.12), transparent 60%), var(--brand-topbar-bg, #ffc434); border-bottom: 1px solid rgba(120, 84, 10, 0.35); box-shadow: 0 5px 16px rgba(72, 50, 8, 0.18); }
.brand { display: flex; align-items: center; gap: 10px; background: transparent; border: 0; color: #1f2937; cursor: pointer; }
.brand img { height: 36px; width: auto; }
.brand__copy { display: grid; gap: 2px; text-align: left; }
.brand__title { font-size: 13px; font-weight: 800; letter-spacing: 0.3px; text-transform: uppercase; }
.brand__sub { font-size: 11px; color: #4a390f; letter-spacing: 0.2px; font-weight: 700; }
.topbar__status { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; }
.topbar__actions { display: flex; align-items: center; gap: 10px; }
.layout { display: grid; grid-template-columns: 340px 1fr 340px; height: calc(100vh - 72px); }
.layout.detail-hidden { grid-template-columns: 340px 1fr; }
.mapwrap { position: relative; background: #cfd8e3; height: 100%; }
.map { width: 100%; height: 100%; }
.chip, .status-pill { font-size: 11px; border-radius: 999px; border: 1px solid rgba(122, 87, 14, 0.35); background: rgba(255, 247, 214, 0.95); color: #4f3a08; padding: 6px 9px; cursor: pointer; font-weight: 700; }
.chip.active, .status-pill.active { background: #1d4ed8; color: #fff; border-color: #1d4ed8; }
.chip.solid { background: #111827; color: #ffdd66; border-color: #111827; }
.alert-btn { position: relative; }
.alert-btn.pulse::after {
  content: '';
  position: absolute;
  top: -3px;
  right: -3px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #dc2626;
  box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.7);
  animation: pulse-alert 1.4s infinite;
}
.chip:disabled { opacity: 0.65; cursor: default; }
.status-badge { font-size: 11px; font-weight: 700; border-radius: 999px; padding: 4px 8px; }
.status-badge[data-state='ok'] { color: #0f5132; background: rgba(22, 163, 74, 0.24); border: 1px solid rgba(22, 163, 74, 0.45); }
.status-badge[data-state='warn'] { color: #7c2d12; background: rgba(251, 191, 36, 0.35); border: 1px solid rgba(217, 119, 6, 0.45); }
.status-badge[data-state='error'] { color: #7f1d1d; background: rgba(239, 68, 68, 0.24); border: 1px solid rgba(220, 38, 38, 0.45); }
.muted { font-size: 11px; color: #64748b; }
.command { display: flex; align-items: center; gap: 8px; width: min(46vw, 560px); background: rgba(255, 255, 255, 0.92); border: 1px solid rgba(120, 84, 10, 0.32); border-radius: 12px; padding: 8px 12px; }
.command svg { width: 18px; height: 18px; opacity: 0.65; fill: #64748b; }
.command input { flex: 1; border: 0; outline: none; background: transparent; color: #0f172a; font-size: 14px; }
.icon-btn { display: grid; place-items: center; width: 40px; height: 40px; border-radius: 11px; background: rgba(255, 255, 255, 0.92); border: 1px solid rgba(120, 84, 10, 0.32); color: #3b2f14; cursor: pointer; }
.icon-btn svg { width: 19px; height: 19px; fill: currentColor; }
.side { background: rgba(255, 250, 232, 0.95); border-right: 1px solid rgba(122, 87, 14, 0.3); display: flex; flex-direction: column; overflow: hidden; }
.side__header { display: flex; align-items: center; justify-content: space-between; padding: 12px; border-bottom: 1px solid rgba(122, 87, 14, 0.3); }
.side__header h3 { margin: 0; color: #0f172a; font-size: 14px; }
.count { font-size: 12px; color: #1e3a8a; font-weight: 800; }
.filters, .status-filters { padding: 8px 10px; display: flex; flex-wrap: wrap; gap: 6px; }
.status-filters { padding-top: 0; }
.list { overflow-y: auto; padding: 10px; display: grid; gap: 8px; }
.list__item { display: grid; grid-template-columns: 36px 1fr auto; align-items: center; gap: 10px; text-align: left; background: #fffaf0; border: 1px solid rgba(122, 87, 14, 0.28); border-radius: 12px; padding: 10px; cursor: pointer; }
.list__item.active { border-color: #1d4ed8; background: rgba(29, 78, 216, 0.1); box-shadow: 0 0 0 1px rgba(29, 78, 216, 0.18); }
.avatar { width: 36px; height: 36px; border-radius: 50%; display: grid; place-items: center; font-size: 14px; }
.avatar[data-state='moving'] { background: rgba(22, 163, 74, 0.24); }
.avatar[data-state='stopped'] { background: rgba(245, 158, 11, 0.24); }
.title { display: flex; align-items: center; gap: 6px; font-size: 13px; font-weight: 700; color: #0f172a; }
.status-chip { font-size: 10px; border-radius: 999px; padding: 2px 6px; font-weight: 700; }
.status-chip[data-tone='ok'] { color: #14532d; background: rgba(74, 222, 128, 0.8); }
.status-chip[data-tone='warn'] { color: #78350f; background: rgba(253, 224, 71, 0.8); }
.status-chip[data-tone='critical'] { color: #7f1d1d; background: rgba(252, 165, 165, 0.9); }
.status-chip[data-tone='info'] { color: #1e3a8a; background: rgba(147, 197, 253, 0.9); }
.status-chip[data-tone='neutral'] { color: #111827; background: rgba(209, 213, 219, 0.9); }
.sub { font-size: 11px; color: #475569; }
.right { display: flex; align-items: center; gap: 8px; }
.badge { font-size: 11px; padding: 4px 8px; border-radius: 999px; background: rgba(17, 24, 39, 0.9); color: #ffdd66; font-weight: 700; }
.detail { background: rgba(255, 250, 232, 0.95); border-left: 1px solid rgba(122, 87, 14, 0.3); display: flex; flex-direction: column; min-width: 320px; }
.detail__header { display: flex; justify-content: space-between; gap: 8px; align-items: center; padding: 12px; border-bottom: 1px solid rgba(122, 87, 14, 0.3); }
.detail__header h3 { margin: 0; color: #0f172a; font-size: 14px; }
.detail__body { display: grid; gap: 12px; padding: 12px; }
.title-row { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.title-row h4 { margin: 0; font-size: 16px; color: #0f172a; }
.meta-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; }
.cell { border: 1px solid rgba(122, 87, 14, 0.3); background: #fffef9; border-radius: 10px; padding: 8px; display: grid; gap: 3px; }
.cell span { font-size: 10px; color: #64748b; text-transform: uppercase; letter-spacing: 0.3px; }
.cell strong { font-size: 13px; color: #0f172a; }
.actions { display: flex; flex-wrap: wrap; gap: 8px; }
.btn { border: 1px solid rgba(122, 87, 14, 0.35); border-radius: 9px; background: #fff6cf; color: #4f3a08; font-size: 12px; padding: 6px 9px; cursor: pointer; font-weight: 700; }
.btn.active { background: #1d4ed8; color: #fff; }
.note-btn { background: #dbeafe; color: #1e3a8a; border-color: #3b82f6; }
.overlay { position: absolute; left: 10px; right: 10px; bottom: 10px; display: flex; justify-content: flex-start; align-items: end; gap: 10px; pointer-events: none; }
.overlay > * { pointer-events: auto; }
.cards { display: flex; flex-wrap: wrap; gap: 10px; }
.kpi-help-wrap { position: relative; align-self: center; margin-left: 2px; }
.kpi-help-btn {
  border: 1px solid rgba(122, 87, 14, 0.35);
  border-radius: 999px;
  background: #fff6cf;
  color: #4f3a08;
  font-size: 11px;
  font-weight: 800;
  padding: 6px 10px;
  cursor: help;
}
.kpi-help-tooltip {
  position: absolute;
  right: 0;
  bottom: calc(100% + 8px);
  width: min(420px, 82vw);
  background: #fffef9;
  border: 1px solid rgba(122, 87, 14, 0.35);
  border-radius: 10px;
  box-shadow: 0 12px 26px rgba(15, 23, 42, 0.2);
  padding: 9px 10px;
  opacity: 0;
  transform: translateY(6px);
  pointer-events: none;
  transition: opacity 0.18s ease, transform 0.18s ease;
  z-index: 25;
}
.kpi-help-tooltip p {
  margin: 0 0 5px 0;
  font-size: 11px;
  color: #1f2937;
  line-height: 1.35;
}
.kpi-help-tooltip p:last-child { margin-bottom: 0; }
.kpi-help-wrap:hover .kpi-help-tooltip,
.kpi-help-wrap:focus-within .kpi-help-tooltip {
  opacity: 1;
  transform: translateY(0);
}
.stat { min-width: 118px; display: grid; gap: 3px; border-radius: 12px; padding: 10px 12px; border: 1px solid #9a6700; background: #ffe9ab; }
.stat[data-tone='ok'] { background: #86efac; border-color: #15803d; }
.stat[data-tone='warn'] { background: #facc15; border-color: #a16207; }
.stat[data-tone='critical'] { background: #fca5a5; border-color: #b91c1c; }
.stat[data-tone='available'] { background: #bbf7d0; border-color: #15803d; }
.stat[data-tone='busy'] { background: #fde68a; border-color: #b45309; }
.stat[data-tone='service'] { background: #bfdbfe; border-color: #1d4ed8; }
.kpi { color: #111827; font-size: 22px; font-weight: 900; line-height: 1; }
.lbl { color: #1f2937; font-size: 10px; letter-spacing: 0.45px; text-transform: uppercase; font-weight: 800; }
.items { display: flex; align-items: center; gap: 7px; overflow-x: auto; flex-wrap: wrap; }
.item { border-radius: 999px; border: 1px solid rgba(122, 87, 14, 0.22); background: #fff6d9; color: #4f3a08; font-size: 11px; padding: 5px 9px; white-space: nowrap; cursor: pointer; font-weight: 700; }
.item[data-severity='high'] { color: #991b1b; border-color: rgba(239, 68, 68, 0.45); background: rgba(239, 68, 68, 0.12); }
.item[data-severity='medium'] { color: #854d0e; border-color: rgba(245, 158, 11, 0.45); background: rgba(245, 158, 11, 0.14); }
.item[data-severity='low'] { color: #1e3a8a; border-color: rgba(59, 130, 246, 0.45); background: rgba(59, 130, 246, 0.12); }
.stale-banner { position: absolute; top: 10px; left: 50%; transform: translateX(-50%); z-index: 12; padding: 7px 11px; border-radius: 999px; font-size: 12px; color: #78350f; background: rgba(253, 230, 138, 0.95); border: 1px solid rgba(245, 158, 11, 0.6); display: grid; gap: 2px; text-align: center; }
.stale-meta { font-size: 10px; opacity: 0.9; }
.empty, .empty-inline { color: #64748b; font-size: 12px; }
.alerts-popup {
  position: fixed;
  top: 82px;
  right: 12px;
  width: min(560px, 78vw);
  max-height: 64vh;
  overflow: auto;
  z-index: 60;
  border-radius: 14px;
  border: 1px solid rgba(122, 87, 14, 0.4);
  background: #fffaf0;
  box-shadow: 0 20px 36px rgba(31, 41, 55, 0.26);
  padding: 10px;
}
.alerts-popup__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}
.alerts-popup__head h4 {
  margin: 0;
  font-size: 13px;
  color: #1f2937;
}
.alerts-popup__list { display: grid; gap: 7px; }
.close-btn {
  width: 30px;
  height: 30px;
  font-size: 13px;
  padding: 0;
}
.alert-pop-enter-active, .alert-pop-leave-active { transition: all 0.2s ease; }
.alert-pop-enter-from, .alert-pop-leave-to { opacity: 0; transform: translateY(-6px) scale(0.98); }
@keyframes pulse-alert {
  0% { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.65); }
  70% { box-shadow: 0 0 0 10px rgba(220, 38, 38, 0); }
  100% { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0); }
}
@media (max-width: 1220px) {
  .layout { grid-template-columns: 1fr; height: calc(100vh - 108px); }
  .side { position: fixed; top: 72px; bottom: 0; left: 0; width: min(92vw, 420px); transform: translateX(0); transition: transform 0.2s ease-out; z-index: 30; }
  .detail { position: fixed; right: 10px; bottom: 10px; width: min(96vw, 420px); max-height: 56vh; border: 1px solid rgba(100, 116, 139, 0.3); border-radius: 14px; overflow: auto; z-index: 20; }
  .overlay { flex-direction: column; align-items: stretch; }
  .kpi-help-wrap { align-self: flex-start; }
  .kpi-help-tooltip { right: auto; left: 0; }
}
</style>
